import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";

/**
 * Drop files from the computer onto this area, as well as choosing them with a button inside it.
 * While a file is over the area, it shows `label` ("Drop the photo here"). The screen checks the
 * files it gets in `onFiles`. A drop somewhere else on the page is stopped, so the browser never
 * leaves the page to open the file.
 */
export function FileDropZone({
  label,
  onFiles,
  disabled = false,
  className = "",
  children,
}: {
  /** The words on the area while a file is over it. */
  label: string;
  /** Every dropped file, in drop order. Empty when the drop held no file (a link, some text). */
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  // dragenter and dragleave fire for every child, so the area counts them.
  const depth = useRef(0);

  // A file let go outside the area must not replace the page.
  useEffect(() => {
    const stop = (event: globalThis.DragEvent) => {
      if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
    };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    return () => {
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
    };
  }, []);

  const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes("Files");

  function onDragEnter(event: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setOver(true);
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  function onDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    depth.current = 0;
    setOver(false);
    if (disabled) return;
    onFiles(Array.from(event.dataTransfer.files));
  }

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative rounded-card ${className}`}
    >
      {children}
      {over ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-card border-2 border-dashed border-accent bg-accent-surface/90 text-[15px] font-semibold text-accent-strong"
        >
          {label}
        </div>
      ) : null}
    </div>
  );
}
